import type { ValueGetterParams } from 'ag-grid-community'

import { computeZscore } from './utils'

const buildKpi = (payload: ValueGetterParams): string | null | number | number[] => {
  if (payload.data == null) {
    return null
  }
  if (payload.colDef == null) {
    return null
  }
  if (payload.colDef.field == null) {
    return null
  }

  switch (payload.colDef.field) {
    case 'max_dai':
      if (payload.data.max_dai == null) {
        payload.data.max_dai = 0
      }
      return payload.data.max_dai

    case 'installs_quantity':
      if (payload.data.ua_installs == null) {
        payload.data.ua_installs = 0
      }
      return payload.data.ua_installs

    case 'mwm_installs':
      if (payload.data.mwm_installs == null) {
        payload.data.mwm_installs = 0
      }
      return payload.data.mwm_installs

    case 'total_spent':
      if (payload.data.ua_spend == null) {
        payload.data.ua_spend = 0
      }
      return payload.data.ua_spend

    case 'impressions':
      if (payload.data.ua_impressions == null) {
        payload.data.ua_impressions = 0
      }
      return payload.data.ua_impressions

    case 'clicks':
      if (payload.data.ua_clicks == null) {
        payload.data.ua_clicks = 0
      }
      return payload.data.ua_clicks

    case 'cpm':
      if (payload.data.ua_spend == null) {
        return 0
      }
      if (payload.data.ua_impressions == null || payload.data.ua_impressions <= 0) {
        return null
      }
      return (payload.data.ua_spend / payload.data.ua_impressions) * 1000

    case 'cvr':
      if (payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_clicks == null || payload.data.ua_clicks <= 0) {
        return null
      }
      return payload.data.ua_installs / payload.data.ua_clicks

    case 'ctr':
      if (payload.data.ua_clicks == null) {
        return null
      }
      if (payload.data.ua_impressions == null || payload.data.ua_impressions <= 0) {
        return null
      }
      return payload.data.ua_clicks / payload.data.ua_impressions

    case 'cpi':
      if (payload.data.ua_spend == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_spend <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_spend / payload.data.ua_installs

    case 'extrapolation':
      if (payload.data.ua_installs == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.ua_installs <= 0 || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_installs / payload.data.mwm_installs

    case 'ua_eltv_d0':
      if (payload.data.ua_total_pred_d0_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d0_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_pred_d0_360_eur / payload.data.ua_installs

    case 'ua_eroi_percent_d0':
      if (payload.data.ua_total_pred_d0_360_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.ua_total_pred_d0_360_eur <= 0 || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_pred_d0_360_eur / payload.data.ua_spend

    case 'ua_eroi_eur_d0':
      if (payload.data.ua_total_pred_d0_360_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_pred_d0_360_eur - payload.data.ua_spend

    case 'ua_iap_eltv_d0':
      if (payload.data.ua_total_inapps_pred_d0_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d0_360_eur / payload.data.ua_installs

    case 'ua_iap_eltv_sov_d0':
      // Compute ua_eltv_d0
      if (payload.data.ua_total_pred_d0_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d0_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      const ua_eltv_d0 = payload.data.ua_total_pred_d0_360_eur / payload.data.ua_installs

      // Compute ua_iap_eltv_d0
      if (payload.data.ua_total_inapps_pred_d0_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_inapps_pred_d0_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      const ua_iap_eltv_d0 = payload.data.ua_total_inapps_pred_d0_360_eur / payload.data.ua_installs

      // Compute ua_iap_eltv_sov_d0
      return ua_iap_eltv_d0 / ua_eltv_d0

    case 'ua_total_ads_pred_per_install_d0_360_eur':
      if (payload.data.ua_total_ads_pred_d0_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_ads_pred_d0_360_eur / payload.data.ua_installs

    case 'mwm_total_pred_d0_360_eur':
      if (payload.data.mwm_total_pred_d0_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.mwm_total_pred_d0_360_eur <= 0 || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_total_pred_d0_360_eur / payload.data.mwm_installs

    case 'ua_eltv_d3':
      if (payload.data.ua_total_pred_d3_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d3_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_pred_d3_360_eur / payload.data.ua_installs

    case 'ua_eroi_percent_d3':
      if (payload.data.ua_total_pred_d3_360_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.ua_total_pred_d3_360_eur <= 0 || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_pred_d3_360_eur / payload.data.ua_spend

    case 'ua_eroi_eur_d3':
      if (payload.data.ua_total_pred_d3_360_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_pred_d3_360_eur - payload.data.ua_spend

    case 'ua_iap_eltv_d3':
      if (payload.data.ua_total_inapps_pred_d3_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d3_360_eur / payload.data.ua_installs

    case 'ua_iap_eltv_sov_d3':
      // Compute ua_eltv_d3
      if (payload.data.ua_total_pred_d3_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d3_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      const ua_eltv_d3 = payload.data.ua_total_pred_d3_360_eur / payload.data.ua_installs

      // Compute ua_iap_eltv_d3
      if (payload.data.ua_total_inapps_pred_d3_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_inapps_pred_d3_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      const ua_iap_eltv_d3 = payload.data.ua_total_inapps_pred_d3_360_eur / payload.data.ua_installs

      // Compute ua_iap_eltv_sov_d3
      return ua_iap_eltv_d3 / ua_eltv_d3

    case 'ua_total_ads_pred_per_install_d3_360_eur':
      if (payload.data.ua_total_ads_pred_d3_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_ads_pred_d3_360_eur / payload.data.ua_installs

    case 'mwm_total_pred_d3_360_eur':
      if (payload.data.mwm_total_pred_d3_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.mwm_total_pred_d3_360_eur <= 0 || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.mwm_total_pred_d3_360_eur / payload.data.mwm_installs

    case 'ua_eltv_d7':
      if (payload.data.ua_total_pred_d7_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d7_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_pred_d7_360_eur / payload.data.ua_installs

    case 'ua_eroi_percent_d7':
      if (payload.data.ua_total_pred_d7_360_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.ua_total_pred_d7_360_eur <= 0 || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_pred_d7_360_eur / payload.data.ua_spend

    case 'ua_eroi_eur_d7':
      if (payload.data.ua_total_pred_d7_360_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_pred_d7_360_eur - payload.data.ua_spend

    case 'ua_iap_eltv_d7':
      if (payload.data.ua_total_inapps_pred_d7_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d7_360_eur / payload.data.ua_installs

    case 'ua_iap_eltv_sov_d7':
      // Compute ua_eltv_d7
      if (payload.data.ua_total_pred_d7_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d7_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      const ua_eltv_d7 = payload.data.ua_total_pred_d7_360_eur / payload.data.ua_installs

      // Compute ua_iap_eltv_d7
      if (payload.data.ua_total_inapps_pred_d7_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_inapps_pred_d7_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      const ua_iap_eltv_d7 = payload.data.ua_total_inapps_pred_d7_360_eur / payload.data.ua_installs

      // Compute ua_iap_eltv_sov_d7
      return ua_iap_eltv_d7 / ua_eltv_d7

    case 'ua_total_ads_pred_d7_per_install_360_eur':
      if (payload.data.ua_total_ads_pred_d7_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_ads_pred_d7_360_eur / payload.data.ua_installs

    case 'mwm_total_pred_d7_360_eur':
      if (payload.data.mwm_total_pred_d7_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.mwm_total_pred_d7_360_eur == 0 || payload.data.mwm_installs == 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.mwm_total_pred_d7_360_eur / payload.data.mwm_installs

    case 'ua_eltv_d10':
      if (payload.data.ua_total_pred_d10_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d10_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_pred_d10_360_eur / payload.data.ua_installs

    case 'ua_eroi_percent_d10':
      if (payload.data.ua_total_pred_d10_360_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.ua_total_pred_d10_360_eur <= 0 || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_pred_d10_360_eur / payload.data.ua_spend

    case 'ua_eroi_eur_d10':
      if (payload.data.ua_total_pred_d10_360_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_pred_d10_360_eur - payload.data.ua_spend

    case 'ua_iap_eltv_d10':
      if (payload.data.ua_total_inapps_pred_d10_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d10_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d10_360_eur / payload.data.ua_installs

    case 'ua_iap_eltv_sov_d10':
      // Compute ua_eltv_d10
      if (payload.data.ua_total_pred_d10_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d10_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      const ua_eltv_d10 = payload.data.ua_total_pred_d10_360_eur / payload.data.ua_installs

      // Compute ua_iap_eltv_d10
      if (payload.data.ua_total_inapps_pred_d10_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_inapps_pred_d10_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      const ua_iap_eltv_d10 = payload.data.ua_total_inapps_pred_d10_360_eur / payload.data.ua_installs

      // Compute ua_iap_eltv_sov_d10
      return ua_iap_eltv_d10 / ua_eltv_d10

    case 'ua_total_ads_pred_per_install_d10_360_eur':
      if (payload.data.ua_total_ads_pred_d10_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_ads_pred_d10_360_eur / payload.data.ua_installs

    case 'mwm_total_pred_d10_360_eur':
      if (payload.data.mwm_total_pred_d10_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.mwm_total_pred_d10_360_eur == 0 || payload.data.mwm_installs == 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_total_pred_d10_360_eur / payload.data.mwm_installs

    case 'mwm_eltv_d10_per_freetrial_lifetime':
      if (payload.data.mwm_total_pred_d10_360_eur === null || payload.data.mwm_nbu_free_trial_d360 === null) {
        return null
      }
      if (payload.data.mwm_total_pred_d10_360_eur <= 0 || payload.data.mwm_nbu_free_trial_d360 <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_total_pred_d10_360_eur / payload.data.mwm_nbu_free_trial_d360

    case 'ua_eltv_d30':
      if (payload.data.ua_total_pred_d30_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d30_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_pred_d30_360_eur / payload.data.ua_installs

    case 'ua_eroi_percent_d30':
      if (payload.data.ua_total_pred_d30_360_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.ua_total_pred_d30_360_eur <= 0 || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_pred_d30_360_eur / payload.data.ua_spend

    case 'ua_eroi_eur_d30':
      if (payload.data.ua_total_pred_d30_360_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_pred_d30_360_eur - payload.data.ua_spend

    case 'ua_iap_eltv_d30':
      if (payload.data.ua_total_inapps_pred_d30_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d30_360_eur / payload.data.ua_installs

    case 'ua_iap_eltv_sov_d30':
      // Compute ua_eltv_d30
      if (payload.data.ua_total_pred_d30_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_pred_d30_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      const ua_eltv_d30 = payload.data.ua_total_pred_d30_360_eur / payload.data.ua_installs

      // Compute ua_iap_eltv_d30
      if (payload.data.ua_total_inapps_pred_d30_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_inapps_pred_d30_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      const ua_iap_eltv_d30 = payload.data.ua_total_inapps_pred_d30_360_eur / payload.data.ua_installs

      // Compute ua_iap_eltv_sov_d30
      return ua_iap_eltv_d30 / ua_eltv_d30

    case 'ua_total_ads_pred_per_install_d30_360_eur':
      if (payload.data.ua_total_ads_pred_d30_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_ads_pred_d30_360_eur / payload.data.ua_installs

    case 'mwm_total_pred_d30_360_eur':
      if (payload.data.mwm_total_pred_d30_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.mwm_total_pred_d30_360_eur == 0 || payload.data.mwm_installs == 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.mwm_total_pred_d30_360_eur / payload.data.mwm_installs

    case 'mwm_roi_percent':
      if (payload.data.mwm_ads_revenue_total_eur == null) {
        payload.data.mwm_ads_revenue_total_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_total_eur == null) {
        payload.data.mwm_inapps_revenue_total_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return (
        (payload.data.mwm_ads_revenue_total_eur + payload.data.mwm_inapps_revenue_total_eur) / payload.data.ua_spend
      )

    case 'mwm_roi_percent_d0':
      if (payload.data.mwm_ads_revenue_d0_eur == null) {
        payload.data.mwm_ads_revenue_d0_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d0_eur == null) {
        payload.data.mwm_inapps_revenue_d0_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return (payload.data.mwm_ads_revenue_d0_eur + payload.data.mwm_inapps_revenue_d0_eur) / payload.data.ua_spend

    case 'mwm_roi_percent_d3':
      if (payload.data.mwm_ads_revenue_d3_eur == null) {
        payload.data.mwm_ads_revenue_d3_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d3_eur == null) {
        payload.data.mwm_inapps_revenue_d3_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return (payload.data.mwm_ads_revenue_d3_eur + payload.data.mwm_inapps_revenue_d3_eur) / payload.data.ua_spend

    case 'mwm_roi_percent_d7':
      if (payload.data.mwm_ads_revenue_d7_eur == null) {
        payload.data.mwm_ads_revenue_d7_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d7_eur == null) {
        payload.data.mwm_inapps_revenue_d7_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return (payload.data.mwm_ads_revenue_d7_eur + payload.data.mwm_inapps_revenue_d7_eur) / payload.data.ua_spend

    case 'mwm_roi_percent_d10':
      if (payload.data.mwm_ads_revenue_d10_eur == null) {
        payload.data.mwm_ads_revenue_d10_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d10_eur == null) {
        payload.data.mwm_inapps_revenue_d10_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return (payload.data.mwm_ads_revenue_d10_eur + payload.data.mwm_inapps_revenue_d10_eur) / payload.data.ua_spend

    case 'mwm_roi_percent_d20':
      if (payload.data.mwm_ads_revenue_d20_eur == null) {
        payload.data.mwm_ads_revenue_d20_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d20_eur == null) {
        payload.data.mwm_inapps_revenue_d20_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 21) {
        return null
      }
      return (payload.data.mwm_ads_revenue_d20_eur + payload.data.mwm_inapps_revenue_d20_eur) / payload.data.ua_spend

    case 'mwm_roi_percent_d30':
      if (payload.data.mwm_ads_revenue_d30_eur == null) {
        payload.data.mwm_ads_revenue_d30_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d30_eur == null) {
        payload.data.mwm_inapps_revenue_d30_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return (payload.data.mwm_ads_revenue_d30_eur + payload.data.mwm_inapps_revenue_d30_eur) / payload.data.ua_spend

    case 'mwm_roi_eur':
      if (payload.data.mwm_ads_revenue_total_eur == null) {
        payload.data.mwm_ads_revenue_total_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_total_eur == null) {
        payload.data.mwm_inapps_revenue_total_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_ads_revenue_total_eur + payload.data.mwm_inapps_revenue_total_eur - payload.data.ua_spend

    case 'ua_roi_percent':
      if (payload.data.ua_total_revenue_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.ua_total_revenue_eur <= 0 || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_revenue_eur / payload.data.ua_spend

    case 'ua_roi_percent_d0':
      if (payload.data.ua_ads_revenue_d0_eur == null) {
        payload.data.ua_ads_revenue_d0_eur = 0
      }
      if (payload.data.ua_inapps_revenue_d0_eur == null) {
        payload.data.ua_inapps_revenue_d0_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return (payload.data.ua_ads_revenue_d0_eur + payload.data.ua_inapps_revenue_d0_eur) / payload.data.ua_spend

    case 'ua_roi_percent_d3':
      if (payload.data.ua_ads_revenue_d3_eur == null) {
        payload.data.ua_ads_revenue_d3_eur = 0
      }
      if (payload.data.ua_inapps_revenue_d3_eur == null) {
        payload.data.ua_inapps_revenue_d3_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return (payload.data.ua_ads_revenue_d3_eur + payload.data.ua_inapps_revenue_d3_eur) / payload.data.ua_spend

    case 'ua_roi_percent_d7':
      if (payload.data.ua_ads_revenue_d7_eur == null) {
        payload.data.ua_ads_revenue_d7_eur = 0
      }
      if (payload.data.ua_inapps_revenue_d7_eur == null) {
        payload.data.ua_inapps_revenue_d7_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return (payload.data.ua_ads_revenue_d7_eur + payload.data.ua_inapps_revenue_d7_eur) / payload.data.ua_spend

    case 'ua_roi_percent_d10':
      if (payload.data.ua_ads_revenue_d10_eur == null) {
        payload.data.ua_ads_revenue_d10_eur = 0
      }
      if (payload.data.ua_inapps_revenue_d10_eur == null) {
        payload.data.ua_inapps_revenue_d10_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return (payload.data.ua_ads_revenue_d10_eur + payload.data.ua_inapps_revenue_d10_eur) / payload.data.ua_spend

    case 'ua_roi_percent_d20':
      if (payload.data.ua_ads_revenue_d20_eur == null) {
        payload.data.ua_ads_revenue_d20_eur = 0
      }
      if (payload.data.ua_inapps_revenue_d20_eur == null) {
        payload.data.ua_inapps_revenue_d20_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 21) {
        return null
      }
      return (payload.data.ua_ads_revenue_d20_eur + payload.data.ua_inapps_revenue_d20_eur) / payload.data.ua_spend

    case 'ua_roi_percent_d30':
      if (payload.data.ua_ads_revenue_d30_eur == null) {
        payload.data.ua_ads_revenue_d30_eur = 0
      }
      if (payload.data.ua_inapps_revenue_d30_eur == null) {
        payload.data.ua_inapps_revenue_d30_eur = 0
      }
      if (payload.data.ua_spend == null || payload.data.ua_spend <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return (payload.data.ua_ads_revenue_d30_eur + payload.data.ua_inapps_revenue_d30_eur) / payload.data.ua_spend

    case 'ua_roi_eur':
      if (payload.data.ua_total_revenue_eur == null || payload.data.ua_spend == null) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_revenue_eur - payload.data.ua_spend

    case 'mwm_arpu':
      if (payload.data.mwm_ads_revenue_total_eur == null) {
        payload.data.mwm_ads_revenue_total_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_total_eur == null) {
        payload.data.mwm_inapps_revenue_total_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      return (
        (payload.data.mwm_ads_revenue_total_eur + payload.data.mwm_inapps_revenue_total_eur) /
        Math.max(payload.data.mwm_installs, 1)
      )

    case 'mwm_arpu_d0':
      if (payload.data.mwm_ads_revenue_d0_eur == null) {
        payload.data.mwm_ads_revenue_d0_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d0_eur == null) {
        payload.data.mwm_inapps_revenue_d0_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return (
        (payload.data.mwm_ads_revenue_d0_eur + payload.data.mwm_inapps_revenue_d0_eur) /
        Math.max(payload.data.mwm_installs, 1)
      )

    case 'mwm_arpu_d1':
      if (payload.data.mwm_ads_revenue_d1_eur == null) {
        payload.data.mwm_ads_revenue_d1_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d1_eur == null) {
        payload.data.mwm_inapps_revenue_d1_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 2) {
        return null
      }
      return (
        (payload.data.mwm_ads_revenue_d1_eur + payload.data.mwm_inapps_revenue_d1_eur) /
        Math.max(payload.data.mwm_installs, 1)
      )

    case 'mwm_arpu_d3':
      if (payload.data.mwm_ads_revenue_d3_eur == null) {
        payload.data.mwm_ads_revenue_d3_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d3_eur == null) {
        payload.data.mwm_inapps_revenue_d3_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return (
        (payload.data.mwm_ads_revenue_d3_eur + payload.data.mwm_inapps_revenue_d3_eur) /
        Math.max(payload.data.mwm_installs, 1)
      )

    case 'mwm_arpu_d7':
      if (payload.data.mwm_ads_revenue_d7_eur == null) {
        payload.data.mwm_ads_revenue_d7_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d7_eur == null) {
        payload.data.mwm_inapps_revenue_d7_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return (
        (payload.data.mwm_ads_revenue_d7_eur + payload.data.mwm_inapps_revenue_d7_eur) /
        Math.max(payload.data.mwm_installs, 1)
      )

    case 'mwm_arpu_d10':
      if (payload.data.mwm_ads_revenue_d10_eur == null) {
        payload.data.mwm_ads_revenue_d10_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d10_eur == null) {
        payload.data.mwm_inapps_revenue_d10_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return (
        (payload.data.mwm_ads_revenue_d10_eur + payload.data.mwm_inapps_revenue_d10_eur) /
        Math.max(payload.data.mwm_installs, 1)
      )

    case 'mwm_arpu_d30':
      if (payload.data.mwm_ads_revenue_d30_eur == null) {
        payload.data.mwm_ads_revenue_d30_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d30_eur == null) {
        payload.data.mwm_inapps_revenue_d30_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return (
        (payload.data.mwm_ads_revenue_d30_eur + payload.data.mwm_inapps_revenue_d30_eur) /
        Math.max(payload.data.mwm_installs, 1)
      )

    case 'mwm_arpu_d180':
      if (payload.data.mwm_ads_revenue_d180_eur == null) {
        payload.data.mwm_ads_revenue_d180_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d180_eur == null) {
        payload.data.mwm_inapps_revenue_d180_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 181) {
        return null
      }
      return (
        (payload.data.mwm_ads_revenue_d180_eur + payload.data.mwm_inapps_revenue_d180_eur) /
        Math.max(payload.data.mwm_installs, 1)
      )

    case 'mwm_arpu_d360':
      if (payload.data.mwm_ads_revenue_d360_eur == null) {
        payload.data.mwm_ads_revenue_d360_eur = 0
      }
      if (payload.data.mwm_inapps_revenue_d360_eur == null) {
        payload.data.mwm_inapps_revenue_d360_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 361) {
        return null
      }
      return (
        (payload.data.mwm_ads_revenue_d360_eur + payload.data.mwm_inapps_revenue_d360_eur) /
        Math.max(payload.data.mwm_installs, 1)
      )

    case 'mwm_inapps_revenue_per_install_d0_eur':
      if (payload.data.mwm_inapps_revenue_d0_eur == null) {
        payload.data.mwm_inapps_revenue_d0_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d0_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_inapps_revenue_per_install_d1_eur':
      if (payload.data.mwm_inapps_revenue_d1_eur == null) {
        payload.data.mwm_inapps_revenue_d1_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 2) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d1_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_inapps_revenue_per_install_d3_eur':
      if (payload.data.mwm_inapps_revenue_d3_eur == null) {
        payload.data.mwm_inapps_revenue_d3_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d3_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_inapps_revenue_per_install_d7_eur':
      if (payload.data.mwm_inapps_revenue_d7_eur == null) {
        payload.data.mwm_inapps_revenue_d7_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d7_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_inapps_revenue_per_install_d10_eur':
      if (payload.data.mwm_inapps_revenue_d10_eur == null) {
        payload.data.mwm_inapps_revenue_d10_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d10_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_inapps_revenue_per_install_d30_eur':
      if (payload.data.mwm_inapps_revenue_d30_eur == null) {
        payload.data.mwm_inapps_revenue_d30_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d30_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_inapps_revenue_per_install_d180_eur':
      if (payload.data.mwm_inapps_revenue_d180_eur == null) {
        payload.data.mwm_inapps_revenue_d180_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 181) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d180_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_inapps_revenue_per_install_d360_eur':
      if (payload.data.mwm_inapps_revenue_d360_eur == null) {
        payload.data.mwm_inapps_revenue_d360_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 361) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d360_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_inapps_revenue_per_install_total_eur':
      if (payload.data.mwm_inapps_revenue_total_eur == null) {
        payload.data.mwm_inapps_revenue_total_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_inapps_revenue_total_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_ads_revenue_per_install_d0_eur':
      if (payload.data.mwm_ads_revenue_d0_eur == null) {
        payload.data.mwm_ads_revenue_d0_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_ads_revenue_d0_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_ads_revenue_per_install_d1_eur':
      if (payload.data.mwm_ads_revenue_d1_eur == null) {
        payload.data.mwm_ads_revenue_d1_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 2) {
        return null
      }
      return payload.data.mwm_ads_revenue_d1_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_ads_revenue_per_install_d3_eur':
      if (payload.data.mwm_ads_revenue_d3_eur == null) {
        payload.data.mwm_ads_revenue_d3_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.mwm_ads_revenue_d3_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_ads_revenue_per_install_d7_eur':
      if (payload.data.mwm_ads_revenue_d7_eur == null) {
        payload.data.mwm_ads_revenue_d7_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.mwm_ads_revenue_d7_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_ads_revenue_per_install_d10_eur':
      if (payload.data.mwm_ads_revenue_d10_eur == null) {
        payload.data.mwm_ads_revenue_d10_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_ads_revenue_d10_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_ads_revenue_per_install_d30_eur':
      if (payload.data.mwm_ads_revenue_d30_eur == null) {
        payload.data.mwm_ads_revenue_d30_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.mwm_ads_revenue_d30_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_ads_revenue_per_install_d180_eur':
      if (payload.data.mwm_ads_revenue_d180_eur == null) {
        payload.data.mwm_ads_revenue_d180_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 181) {
        return null
      }
      return payload.data.mwm_ads_revenue_d180_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_ads_revenue_per_install_d360_eur':
      if (payload.data.mwm_ads_revenue_d360_eur == null) {
        payload.data.mwm_ads_revenue_d360_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 361) {
        return null
      }
      return payload.data.mwm_ads_revenue_d360_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_ads_revenue_per_install_total_eur':
      if (payload.data.mwm_ads_revenue_total_eur == null) {
        payload.data.mwm_ads_revenue_total_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_ads_revenue_total_eur / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_total_ads_pred_d0_360_eur':
      if (payload.data.mwm_total_ads_pred_d0_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.mwm_total_ads_pred_d0_360_eur <= 0 || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_total_ads_pred_d0_360_eur / payload.data.mwm_installs

    case 'mwm_total_ads_pred_d3_360_eur':
      if (payload.data.mwm_total_ads_pred_d3_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.mwm_total_ads_pred_d3_360_eur <= 0 || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.mwm_total_ads_pred_d3_360_eur / payload.data.mwm_installs

    case 'mwm_total_ads_pred_d7_360_eur':
      if (payload.data.mwm_total_ads_pred_d7_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.mwm_total_ads_pred_d7_360_eur <= 0 || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.mwm_total_ads_pred_d7_360_eur / payload.data.mwm_installs

    case 'mwm_total_ads_pred_d10_360_eur':
      if (payload.data.mwm_total_ads_pred_d10_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.mwm_total_ads_pred_d10_360_eur <= 0 || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_total_ads_pred_d10_360_eur / payload.data.mwm_installs

    case 'mwm_total_ads_pred_d30_360_eur':
      if (payload.data.mwm_total_ads_pred_d30_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.mwm_total_ads_pred_d30_360_eur <= 0 || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.mwm_total_ads_pred_d30_360_eur / payload.data.mwm_installs

    case 'mwm_nbu_free_trial_d0':
      if (payload.data.mwm_nbu_free_trial_d0 === null) {
        payload.data.mwm_nbu_free_trial_d0 = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_nbu_free_trial_d0

    case 'mwm_nbu_free_trial_d10':
      if (payload.data.mwm_nbu_free_trial_d10 === null) {
        payload.data.mwm_nbu_free_trial_d10 = 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_nbu_free_trial_d10

    case 'mwm_nbu_free_trial_lifetime':
      if (payload.data.mwm_nbu_free_trial_d360 === null) {
        payload.data.mwm_nbu_free_trial_d360 = 0
      }

      return payload.data.mwm_nbu_free_trial_d360

    case 'mwm_nbu_paying_user_d0':
      if (payload.data.mwm_nbu_paying_user_d0 === null) {
        payload.data.mwm_nbu_paying_user_d0 = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_nbu_paying_user_d0

    case 'mwm_nbu_paying_user_d10':
      if (payload.data.mwm_nbu_paying_user_d10 === null) {
        payload.data.mwm_nbu_paying_user_d10 = 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_nbu_paying_user_d10

    case 'mwm_nbu_paying_user_lifetime':
      if (payload.data.mwm_nbu_paying_user_d360 === null) {
        payload.data.mwm_nbu_paying_user_d360 = 0
      }

      return payload.data.mwm_nbu_paying_user_d360

    case 'mwm_nbu_subscriber_d0':
      if (payload.data.mwm_nbu_subscriber_d0 === null) {
        payload.data.mwm_nbu_subscriber_d0 = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_nbu_subscriber_d0

    case 'mwm_nbu_subscriber_d10':
      if (payload.data.mwm_nbu_subscriber_d10 === null) {
        payload.data.mwm_nbu_subscriber_d10 = 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_nbu_subscriber_d10

    case 'mwm_nbu_subscriber_lifetime':
      if (payload.data.mwm_nbu_subscriber_d360 === null) {
        payload.data.mwm_nbu_subscriber_d360 = 0
      }

      return payload.data.mwm_nbu_subscriber_d360

    case 'mwm_eltv_d10_per_subscriber_lifetime':
      if (payload.data.mwm_total_pred_d10_360_eur == null || payload.data.mwm_nbu_subscriber_d360 == null) {
        return null
      }
      if (payload.data.mwm_total_pred_d10_360_eur <= 0 || payload.data.mwm_nbu_subscriber_d360 <= 0) {
        return null
      }

      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_total_pred_d10_360_eur / payload.data.mwm_nbu_subscriber_d360

    case 'mwm_cost_per_free_trial_lifetime':
      if (payload.data.mwm_nbu_free_trial_d360 == null) {
        return 0
      }
      if (payload.data.mwm_installs == null) {
        return 0
      }
      if (payload.data.ua_installs == null) {
        return 0
      }
      if (payload.data.ua_spend == null) {
        return 0
      }
      return (
        (payload.data.ua_spend * payload.data.mwm_installs) /
        payload.data.ua_installs /
        Math.max(payload.data.mwm_nbu_free_trial_d360, 1)
      )

    case 'mwm_cost_per_free_trial_d0':
      if (payload.data.mwm_nbu_free_trial_d0 === null) {
        return 0
      }
      if (payload.data.mwm_installs === null) {
        return 0
      }
      if (payload.data.ua_installs === null) {
        return 0
      }
      if (payload.data.ua_spend === null) {
        return 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return (
        (payload.data.ua_spend * payload.data.mwm_installs) /
        payload.data.ua_installs /
        Math.max(payload.data.mwm_nbu_free_trial_d0, 1)
      )

    case 'mwm_cost_per_free_trial_d10':
      if (payload.data.mwm_nbu_free_trial_d10 == null) {
        return 0
      }
      if (payload.data.mwm_installs == null) {
        return 0
      }
      if (payload.data.ua_installs == null) {
        return 0
      }
      if (payload.data.ua_spend == null) {
        return 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return (
        (payload.data.ua_spend * payload.data.mwm_installs) /
        payload.data.ua_installs /
        Math.max(payload.data.mwm_nbu_free_trial_d10, 1)
      )

    case 'mwm_cost_per_paying_user_lifetime':
      if (payload.data.mwm_nbu_paying_user_d360 == null) {
        return 0
      }
      if (payload.data.ua_spend == null) {
        return 0
      }
      return payload.data.ua_spend / Math.max(payload.data.mwm_nbu_paying_user_d360, 1)

    case 'mwm_cost_per_paying_user_d0':
      if (payload.data.mwm_nbu_paying_user_d0 == null) {
        return 0
      }
      if (payload.data.ua_spend == null) {
        return 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_spend / Math.max(payload.data.mwm_nbu_paying_user_d0, 1)

    case 'mwm_cost_per_paying_user_d10':
      if (payload.data.mwm_nbu_paying_user_d10 == null) {
        return 0
      }
      if (payload.data.ua_spend == null) {
        return 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_spend / Math.max(payload.data.mwm_nbu_paying_user_d10, 1)

    case 'mwm_cost_per_paying_user_d360':
      if (payload.data.mwm_nbu_paying_user_d360 == null) {
        return 0
      }
      if (payload.data.ua_spend == null) {
        return 0
      }
      if (payload.data.max_dai < 361) {
        return null
      }
      return payload.data.ua_spend / Math.max(payload.data.mwm_nbu_paying_user_d360, 1)

    case 'mwm_cost_per_subscriber_lifetime':
      if (payload.data.mwm_nbu_subscriber_d360 == null) {
        return 0
      }
      if (payload.data.mwm_installs == null) {
        return 0
      }
      if (payload.data.ua_installs == null) {
        return 0
      }
      if (payload.data.ua_spend == null) {
        return 0
      }
      return (
        (payload.data.ua_spend * payload.data.mwm_installs) /
        payload.data.ua_installs /
        Math.max(payload.data.mwm_nbu_subscriber_d360, 1)
      )

    case 'mwm_cost_per_subscriber_d0':
      if (payload.data.mwm_nbu_subscriber_d0 == null) {
        return 0
      }
      if (payload.data.mwm_installs == null) {
        return 0
      }
      if (payload.data.ua_installs == null) {
        return 0
      }
      if (payload.data.ua_spend == null) {
        return 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return (
        (payload.data.ua_spend * payload.data.mwm_installs) /
        payload.data.ua_installs /
        Math.max(payload.data.mwm_nbu_subscriber_d0, 1)
      )

    case 'mwm_cost_per_subscriber_d10':
      if (payload.data.mwm_nbu_subscriber_d10 == null) {
        return 0
      }
      if (payload.data.mwm_installs == null) {
        return 0
      }
      if (payload.data.ua_installs == null) {
        return 0
      }
      if (payload.data.ua_spend == null) {
        return 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return (
        (payload.data.ua_spend * payload.data.mwm_installs) /
        payload.data.ua_installs /
        Math.max(payload.data.mwm_nbu_subscriber_d10, 1)
      )

    case 'mwm_cost_per_subscriber_d360':
      if (payload.data.mwm_nbu_subscriber_d360 == null) {
        return 0
      }
      if (payload.data.mwm_installs == null) {
        return 0
      }
      if (payload.data.ua_installs == null) {
        return 0
      }
      if (payload.data.ua_spend == null) {
        return 0
      }
      if (payload.data.max_dai < 361) {
        return null
      }
      return (
        (payload.data.ua_spend * payload.data.mwm_installs) /
        payload.data.ua_installs /
        Math.max(payload.data.mwm_nbu_subscriber_d360, 1)
      )

    case 'mwm_revenue_per_free_trial_lifetime':
      if (payload.data.mwm_nbu_free_trial_d360 == null) {
        return null
      }
      if (payload.data.mwm_inapps_revenue_d360_eur == null) {
        payload.data.mwm_inapps_revenue_d360_eur = 0
      }
      return payload.data.mwm_inapps_revenue_d360_eur / Math.max(payload.data.mwm_nbu_free_trial_d360, 1)

    case 'mwm_eltv_d10_per_paying_lifetime':
      if (payload.data.mwm_nbu_paying_user_d360 === null) {
        return null
      }
      if (payload.data.mwm_total_pred_d10_360_eur === null) {
        payload.data.mwm_total_pred_d10_360_eur = 0
      }
      return payload.data.mwm_total_pred_d10_360_eur / Math.max(payload.data.mwm_nbu_paying_user_d360, 1)

    case 'mwm_revenue_per_paying_user_lifetime':
      if (payload.data.mwm_nbu_paying_user_d360 == null) {
        return null
      }
      if (payload.data.mwm_inapps_revenue_d360_eur == null) {
        payload.data.mwm_inapps_revenue_d360_eur = 0
      }
      return payload.data.mwm_inapps_revenue_d360_eur / Math.max(payload.data.mwm_nbu_paying_user_d360, 1)

    case 'mwm_revenue_per_subscriber_lifetime':
      if (payload.data.mwm_nbu_subscriber_d360 == null) {
        return null
      }
      if (payload.data.mwm_inapps_revenue_d360_eur == null) {
        payload.data.mwm_inapps_revenue_d360_eur = 0
      }
      return payload.data.mwm_inapps_revenue_d360_eur / Math.max(payload.data.mwm_nbu_subscriber_d360, 1)

    case 'mwm_free_trial_per_install_lifetime':
      if (payload.data.mwm_nbu_free_trial_d360 === null) {
        payload.data.mwm_nbu_free_trial_d360 = 0
      }
      if (payload.data.mwm_installs === null) {
        payload.data.mwm_installs = 0
      }
      return payload.data.mwm_nbu_free_trial_d360 / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_free_trial_per_install_d0':
      if (payload.data.mwm_nbu_free_trial_d0 == null) {
        payload.data.mwm_nbu_free_trial_d0 = 0
      }
      if (payload.data.mwm_installs == null) {
        payload.data.mwm_installs = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_nbu_free_trial_d0 / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_free_trial_per_install_d10':
      if (payload.data.mwm_nbu_free_trial_d10 == null) {
        payload.data.mwm_nbu_free_trial_d10 = 0
      }
      if (payload.data.mwm_installs == null) {
        payload.data.mwm_installs = 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_nbu_free_trial_d10 / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_paying_user_per_install_lifetime':
      if (payload.data.mwm_nbu_paying_user_d360 === null) {
        payload.data.mwm_nbu_paying_user_d360 = 0
      }
      if (payload.data.mwm_installs === null) {
        payload.data.mwm_installs = 0
      }

      return payload.data.mwm_nbu_paying_user_d360 / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_paying_user_per_install_d0':
      if (payload.data.mwm_nbu_paying_user_d0 == null) {
        payload.data.mwm_nbu_paying_user_d0 = 0
      }
      if (payload.data.mwm_installs == null) {
        payload.data.mwm_installs = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_nbu_paying_user_d0 / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_paying_user_per_install_d10':
      if (payload.data.mwm_nbu_paying_user_d10 == null) {
        payload.data.mwm_nbu_paying_user_d10 = 0
      }
      if (payload.data.mwm_installs == null) {
        payload.data.mwm_installs = 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_nbu_paying_user_d10 / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_subscriber_per_install_lifetime':
      if (payload.data.mwm_nbu_subscriber_d360 === null) {
        payload.data.mwm_nbu_subscriber_d360 = 0
      }
      if (payload.data.mwm_installs === null) {
        payload.data.mwm_installs = 0
      }
      return payload.data.mwm_nbu_subscriber_d360 / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_subscriber_per_install_d0':
      if (payload.data.mwm_nbu_subscriber_d0 == null) {
        payload.data.mwm_nbu_subscriber_d0 = 0
      }
      if (payload.data.mwm_installs == null) {
        payload.data.mwm_installs = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_nbu_subscriber_d0 / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_subscriber_per_install_d10':
      if (payload.data.mwm_nbu_subscriber_d10 == null) {
        payload.data.mwm_nbu_subscriber_d10 = 0
      }
      if (payload.data.mwm_installs == null) {
        payload.data.mwm_installs = 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_nbu_subscriber_d10 / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_inapps_revenue_total_eur':
      if (payload.data.mwm_inapps_revenue_total_eur == null) {
        payload.data.mwm_inapps_revenue_total_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_inapps_revenue_total_eur

    case 'mwm_inapps_revenue_d0_eur':
      if (payload.data.mwm_inapps_revenue_d0_eur == null) {
        payload.data.mwm_inapps_revenue_d0_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d0_eur

    case 'mwm_inapps_revenue_d1_eur':
      if (payload.data.mwm_inapps_revenue_d1_eur == null) {
        payload.data.mwm_inapps_revenue_d1_eur = 0
      }
      if (payload.data.max_dai < 2) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d1_eur

    case 'mwm_inapps_revenue_d3_eur':
      if (payload.data.mwm_inapps_revenue_d3_eur == null) {
        payload.data.mwm_inapps_revenue_d3_eur = 0
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d3_eur

    case 'mwm_inapps_revenue_d7_eur':
      if (payload.data.mwm_inapps_revenue_d7_eur == null) {
        payload.data.mwm_inapps_revenue_d7_eur = 0
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d7_eur

    case 'mwm_inapps_revenue_d30_eur':
      if (payload.data.mwm_inapps_revenue_d30_eur == null) {
        payload.data.mwm_inapps_revenue_d30_eur = 0
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d30_eur

    case 'mwm_inapps_revenue_d180_eur':
      if (payload.data.mwm_inapps_revenue_d180_eur == null) {
        payload.data.mwm_inapps_revenue_d180_eur = 0
      }
      if (payload.data.max_dai < 181) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d180_eur

    case 'mwm_inapps_revenue_d360_eur':
      if (payload.data.mwm_inapps_revenue_d360_eur == null) {
        payload.data.mwm_inapps_revenue_d360_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 361) {
        return null
      }
      return payload.data.mwm_inapps_revenue_d360_eur

    case 'ua_inapps_revenue_total_eur':
      if (payload.data.ua_inapps_revenue_total_eur == null) {
        payload.data.ua_inapps_revenue_total_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_inapps_revenue_total_eur

    case 'ua_inapps_revenue_d0_eur':
      if (payload.data.ua_inapps_revenue_d0_eur == null) {
        payload.data.ua_inapps_revenue_d0_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_inapps_revenue_d0_eur

    case 'ua_inapps_revenue_d1_eur':
      if (payload.data.ua_inapps_revenue_d1_eur == null) {
        payload.data.ua_inapps_revenue_d1_eur = 0
      }
      if (payload.data.max_dai < 2) {
        return null
      }
      return payload.data.ua_inapps_revenue_d1_eur

    case 'ua_inapps_revenue_d3_eur':
      if (payload.data.ua_inapps_revenue_d3_eur == null) {
        payload.data.ua_inapps_revenue_d3_eur = 0
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_inapps_revenue_d3_eur

    case 'ua_inapps_revenue_d7_eur':
      if (payload.data.ua_inapps_revenue_d7_eur == null) {
        payload.data.ua_inapps_revenue_d7_eur = 0
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_inapps_revenue_d7_eur

    case 'ua_inapps_revenue_d30_eur':
      if (payload.data.ua_inapps_revenue_d30_eur == null) {
        payload.data.ua_inapps_revenue_d30_eur = 0
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_inapps_revenue_d30_eur

    case 'ua_inapps_revenue_d180_eur':
      if (payload.data.ua_inapps_revenue_d180_eur == null) {
        payload.data.ua_inapps_revenue_d180_eur = 0
      }
      if (payload.data.max_dai < 181) {
        return null
      }
      return payload.data.ua_inapps_revenue_d180_eur

    case 'ua_inapps_revenue_d360_eur':
      if (payload.data.ua_inapps_revenue_d360_eur == null) {
        payload.data.ua_inapps_revenue_d360_eur = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 361) {
        return null
      }
      return payload.data.ua_inapps_revenue_d360_eur

    case 'mwm_ads_revenue_total_eur':
      if (payload.data.mwm_ads_revenue_total_eur == null) {
        payload.data.mwm_ads_revenue_total_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_ads_revenue_total_eur

    case 'mwm_ads_revenue_d0_eur':
      if (payload.data.mwm_ads_revenue_d0_eur == null) {
        payload.data.mwm_ads_revenue_d0_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_ads_revenue_d0_eur

    case 'mwm_ads_revenue_d1_eur':
      if (payload.data.mwm_ads_revenue_d1_eur == null) {
        payload.data.mwm_ads_revenue_d1_eur = 0
      }
      if (payload.data.max_dai < 2) {
        return null
      }
      return payload.data.mwm_ads_revenue_d1_eur

    case 'mwm_ads_revenue_d3_eur':
      if (payload.data.mwm_ads_revenue_d3_eur == null) {
        payload.data.mwm_ads_revenue_d3_eur = 0
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.mwm_ads_revenue_d3_eur

    case 'mwm_ads_revenue_d7_eur':
      if (payload.data.mwm_ads_revenue_d7_eur == null) {
        payload.data.mwm_ads_revenue_d7_eur = 0
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.mwm_ads_revenue_d7_eur

    case 'mwm_ads_revenue_d30_eur':
      if (payload.data.mwm_ads_revenue_d30_eur == null) {
        payload.data.mwm_ads_revenue_d30_eur = 0
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.mwm_ads_revenue_d30_eur

    case 'mwm_ads_revenue_d180_eur':
      if (payload.data.mwm_ads_revenue_d180_eur == null) {
        payload.data.mwm_ds_revenue_d180_eur = 0
      }
      if (payload.data.max_dai < 181) {
        return null
      }
      return payload.data.mwm_ads_revenue_d180_eur

    case 'mwm_ads_revenue_d360_eur':
      if (payload.data.mwm_ads_revenue_d360_eur == null) {
        payload.data.mwm_ads_revenue_d360_eur = 0
      }
      if (payload.data.max_dai < 361) {
        return null
      }
      return payload.data.mwm_ads_revenue_d360_eur

    case 'ua_ads_revenue_total_eur':
      if (payload.data.ua_ads_revenue_total_eur == null) {
        payload.data.ua_ads_revenue_total_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_ads_revenue_total_eur

    case 'ua_ads_revenue_d0_eur':
      if (payload.data.ua_ads_revenue_d0_eur == null) {
        payload.data.ua_ads_revenue_d0_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_ads_revenue_d0_eur

    case 'ua_ads_revenue_d1_eur':
      if (payload.data.ua_ads_revenue_d1_eur == null) {
        payload.data.ua_ads_revenue_d1_eur = 0
      }
      if (payload.data.max_dai < 2) {
        return null
      }
      return payload.data.ua_ads_revenue_d1_eur

    case 'ua_ads_revenue_d3_eur':
      if (payload.data.ua_ads_revenue_d3_eur == null) {
        payload.data.ua_ads_revenue_d3_eur = 0
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_ads_revenue_d3_eur

    case 'ua_ads_revenue_d7_eur':
      if (payload.data.ua_ads_revenue_d7_eur == null) {
        payload.data.ua_ads_revenue_d7_eur = 0
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_ads_revenue_d7_eur

    case 'ua_ads_revenue_d30_eur':
      if (payload.data.ua_ads_revenue_d30_eur == null) {
        payload.data.ua_ads_revenue_d30_eur = 0
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_ads_revenue_d30_eur

    case 'ua_ads_revenue_d180_eur':
      if (payload.data.ua_ads_revenue_d180_eur == null) {
        payload.data.ua_ads_revenue_d180_eur = 0
      }
      if (payload.data.max_dai < 181) {
        return null
      }
      return payload.data.ua_ads_revenue_d180_eur

    case 'ua_ads_revenue_d360_eur':
      if (payload.data.ua_ads_revenue_d360_eur == null) {
        payload.data.ua_ads_revenue_d360_eur = 0
      }
      if (payload.data.max_dai < 361) {
        return null
      }
      return payload.data.ua_ads_revenue_d360_eur

    case 'ua_total_inapps_pred_d0_360_eur':
      if (payload.data.ua_total_inapps_pred_d0_360_eur == null) {
        payload.data.ua_total_inapps_pred_d0_360_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d0_360_eur

    case 'ua_total_inapps_pred_d3_360_eur':
      if (payload.data.ua_total_inapps_pred_d3_360_eur == null) {
        payload.data.ua_total_inapps_pred_d3_360_eur = 0
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d3_360_eur

    case 'ua_total_inapps_pred_d7_360_eur':
      if (payload.data.ua_total_inapps_pred_d7_360_eur == null) {
        payload.data.ua_total_inapps_pred_d7_360_eur = 0
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d7_360_eur

    case 'ua_total_inapps_pred_d10_360_eur':
      if (payload.data.ua_total_inapps_pred_d10_360_eur == null) {
        payload.data.ua_total_inapps_pred_d10_360_eur = 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d10_360_eur

    case 'ua_total_inapps_pred_d30_360_eur':
      if (payload.data.ua_total_inapps_pred_d30_360_eur == null) {
        payload.data.ua_total_inapps_pred_d30_360_eur = 0
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d30_360_eur

    case 'ua_total_ads_pred_d0_360_eur':
      if (payload.data.ua_total_ads_pred_d0_360_eur == null) {
        payload.data.ua_total_ads_pred_d0_360_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_ads_pred_d0_360_eur

    case 'ua_total_ads_pred_d3_360_eur':
      if (payload.data.ua_total_ads_pred_d3_360_eur == null) {
        payload.data.ua_total_ads_pred_d3_360_eur = 0
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_ads_pred_d3_360_eur

    case 'ua_total_ads_pred_d7_360_eur':
      if (payload.data.ua_total_ads_pred_d7_360_eur == null) {
        payload.data.ua_total_ads_pred_d7_360_eur = 0
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_ads_pred_d7_360_eur

    case 'ua_total_ads_pred_d10_360_eur':
      if (payload.data.ua_total_ads_pred_d10_360_eur == null) {
        payload.data.ua_total_ads_pred_d10_360_eur = 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_ads_pred_d10_360_eur

    case 'ua_total_ads_pred_d30_360_eur':
      if (payload.data.ua_total_ads_pred_d30_360_eur == null) {
        payload.data.ua_total_ads_pred_d30_360_eur = 0
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_ads_pred_d30_360_eur

    case 'ua_total_pred_d0_360_eur':
      if (payload.data.ua_total_pred_d0_360_eur == null) {
        payload.data.ua_total_pred_d0_360_eur = 0
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_pred_d0_360_eur

    case 'ua_total_pred_d3_360_eur':
      if (payload.data.ua_total_pred_d3_360_eur == null) {
        payload.data.ua_total_pred_d3_360_eur = 0
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_pred_d3_360_eur

    case 'ua_total_pred_d7_360_eur':
      if (payload.data.ua_total_pred_d7_360_eur == null) {
        payload.data.ua_total_pred_d7_360_eur = 0
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_pred_d7_360_eur

    case 'ua_total_pred_d10_360_eur':
      if (payload.data.ua_total_pred_d10_360_eur == null) {
        payload.data.ua_total_pred_d10_360_eur = 0
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_pred_d10_360_eur

    case 'ua_total_pred_d20_360_eur':
      if (payload.data.ua_total_pred_d20_360_eur == null) {
        payload.data.ua_total_pred_d20_360_eur = 0
      }
      if (payload.data.max_dai < 21) {
        return null
      }
      return payload.data.ua_total_pred_d20_360_eur

    case 'ua_total_pred_d30_360_eur':
      if (payload.data.ua_total_pred_d30_360_eur == null) {
        payload.data.ua_total_pred_d30_360_eur = 0
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_pred_d30_360_eur

    case 'ua_total_pred_d40_360_eur':
      if (payload.data.ua_total_pred_d40_360_eur == null) {
        payload.data.ua_total_pred_d40_360_eur = 0
      }
      if (payload.data.max_dai < 41) {
        return null
      }
      return payload.data.ua_total_pred_d40_360_eur

    case 'retention_d1':
      if (payload.data.retention_d1 == null) {
        payload.data.retention_d1 = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 2) {
        return null
      }
      return payload.data.retention_d1 / Math.max(payload.data.mwm_installs, 1)

    case 'retention_d3':
      if (payload.data.retention_d3 == null) {
        payload.data.retention_d3 = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.retention_d3 / Math.max(payload.data.mwm_installs, 1)

    case 'retention_d7':
      if (payload.data.retention_d7 == null) {
        payload.data.retention_d7 = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.retention_d7 / Math.max(payload.data.mwm_installs, 1)

    case 'retention_d30':
      if (payload.data.retention_d30 == null) {
        payload.data.retention_d30 = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.retention_d30 / Math.max(payload.data.mwm_installs, 1)

    case 'mwm_nbu_onboarding_completed':
      if (payload.data.mwm_nbu_onboarding_completed == null) {
        payload.data.mwm_nbu_onboarding_completed = 0
      }
      if (payload.data.mwm_installs == null || payload.data.mwm_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_nbu_onboarding_completed / Math.max(payload.data.mwm_installs, 1)

    case 'ua_arpu_ads_pred_d0_360_eur':
      if (payload.data.ua_total_ads_pred_d0_360_eur == null) {
        payload.data.ua_total_ads_pred_d0_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_ads_pred_d0_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_ads_pred_d3_360_eur':
      if (payload.data.ua_total_ads_pred_d3_360_eur == null) {
        payload.data.ua_total_ads_pred_d3_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_ads_pred_d3_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_ads_pred_d7_360_eur':
      if (payload.data.ua_total_ads_pred_d7_360_eur == null) {
        payload.data.ua_total_ads_pred_d7_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_ads_pred_d7_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_ads_pred_d10_360_eur':
      if (payload.data.ua_total_ads_pred_d10_360_eur == null) {
        payload.data.ua_total_ads_pred_d10_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_ads_pred_d10_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_ads_pred_d20_360_eur':
      if (payload.data.ua_total_ads_pred_d20_360_eur == null) {
        payload.data.ua_total_ads_pred_d20_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 21) {
        return null
      }
      return payload.data.ua_total_ads_pred_d20_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_ads_pred_d30_360_eur':
      if (payload.data.ua_total_ads_pred_d30_360_eur == null) {
        payload.data.ua_total_ads_pred_d30_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_ads_pred_d30_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_ads_pred_d40_360_eur':
      if (payload.data.ua_total_ads_pred_d40_360_eur == null) {
        payload.data.ua_total_ads_pred_d40_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 41) {
        return null
      }
      return payload.data.ua_total_ads_pred_d40_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_inapps_pred_d0_360_eur':
      if (payload.data.ua_total_inapps_pred_d0_360_eur == null) {
        payload.data.ua_total_inapps_pred_d0_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d0_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_inapps_pred_d3_360_eur':
      if (payload.data.ua_total_inapps_pred_d3_360_eur == null) {
        payload.data.ua_total_inapps_pred_d3_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d3_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_inapps_pred_d7_360_eur':
      if (payload.data.ua_total_inapps_pred_d7_360_eur == null) {
        payload.data.ua_total_inapps_pred_d7_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d7_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_inapps_pred_d10_360_eur':
      if (payload.data.ua_total_inapps_pred_d10_360_eur == null) {
        payload.data.ua_total_inapps_pred_d10_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d10_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_inapps_pred_d20_360_eur':
      if (payload.data.ua_total_inapps_pred_d20_360_eur == null) {
        payload.data.ua_total_inapps_pred_d20_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 21) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d20_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_inapps_pred_d30_360_eur':
      if (payload.data.ua_total_inapps_pred_d30_360_eur == null) {
        payload.data.ua_total_inapps_pred_d30_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d30_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_inapps_pred_d40_360_eur':
      if (payload.data.ua_total_inapps_pred_d40_360_eur == null) {
        payload.data.ua_total_inapps_pred_d40_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 41) {
        return null
      }
      return payload.data.ua_total_inapps_pred_d40_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_pred_d0_360_eur':
      if (payload.data.ua_total_pred_d0_360_eur == null) {
        payload.data.ua_total_pred_d0_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_pred_d0_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_pred_d3_360_eur':
      if (payload.data.ua_total_pred_d3_360_eur == null) {
        payload.data.ua_total_pred_d3_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_pred_d3_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_pred_d7_360_eur':
      if (payload.data.ua_total_pred_d7_360_eur == null) {
        payload.data.ua_total_pred_d7_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_pred_d7_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_pred_d10_360_eur':
      if (payload.data.ua_total_pred_d10_360_eur == null) {
        payload.data.ua_total_pred_d10_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_pred_d10_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_pred_d20_360_eur':
      if (payload.data.ua_total_pred_d20_360_eur == null) {
        payload.data.ua_total_pred_d20_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 21) {
        return null
      }
      return payload.data.ua_total_pred_d20_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_pred_d30_360_eur':
      if (payload.data.ua_total_pred_d30_360_eur == null) {
        payload.data.ua_total_pred_d30_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_pred_d30_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_arpu_pred_d40_360_eur':
      if (payload.data.ua_total_pred_d40_360_eur == null) {
        payload.data.ua_total_pred_d40_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 41) {
        return null
      }
      return payload.data.ua_total_pred_d40_360_eur / Math.max(payload.data.ua_installs, 1)

    case 'ua_eltv_d0_error_rate':
      if (payload.data.ua_total_pred_variance_d0_360_eur == null) {
        payload.data.ua_total_pred_variance_d0_360_eur = 0
      }
      if (payload.data.ua_total_pred_d0_360_eur == null || payload.data.ua_total_pred_d0_360_eur <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return (
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d0_360_eur)) /
        Math.max(payload.data.ua_total_pred_d0_360_eur, 1)
      )

    case 'ua_eltv_d3_error_rate':
      if (payload.data.ua_total_pred_variance_d3_360_eur == null) {
        payload.data.ua_total_pred_variance_d3_360_eur = 0
      }
      if (payload.data.ua_total_pred_d3_360_eur == null || payload.data.ua_total_pred_d3_360_eur <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return (
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d3_360_eur)) /
        Math.max(payload.data.ua_total_pred_d3_360_eur, 1)
      )

    case 'ua_eltv_d7_error_rate':
      if (payload.data.ua_total_pred_variance_d7_360_eur == null) {
        payload.data.ua_total_pred_variance_d7_360_eur = 0
      }
      if (payload.data.ua_total_pred_d7_360_eur == null || payload.data.ua_total_pred_d7_360_eur <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return (
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d7_360_eur)) /
        Math.max(payload.data.ua_total_pred_d7_360_eur, 1)
      )

    case 'ua_eltv_d10_error_rate':
      if (payload.data.ua_total_pred_variance_d10_360_eur == null) {
        payload.data.ua_total_pred_variance_d10_360_eur = 0
      }
      if (payload.data.ua_total_pred_d10_360_eur == null || payload.data.ua_total_pred_d10_360_eur <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return (
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d10_360_eur)) /
        Math.max(payload.data.ua_total_pred_d10_360_eur, 1)
      )

    case 'ua_eltv_d20_error_rate':
      if (payload.data.ua_total_pred_variance_d20_360_eur == null) {
        payload.data.ua_total_pred_variance_d20_360_eur = 0
      }
      if (payload.data.ua_total_pred_d20_360_eur == null || payload.data.ua_total_pred_d20_360_eur <= 0) {
        return null
      }
      if (payload.data.max_dai < 21) {
        return null
      }
      return (
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d20_360_eur)) /
        Math.max(payload.data.ua_total_pred_d20_360_eur, 1)
      )

    case 'ua_eltv_d30_error_rate':
      if (payload.data.ua_total_pred_variance_d30_360_eur == null) {
        payload.data.ua_total_pred_variance_d30_360_eur = 0
      }
      if (payload.data.ua_total_pred_d30_360_eur == null || payload.data.ua_total_pred_d30_360_eur <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return (
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d30_360_eur)) /
        Math.max(payload.data.ua_total_pred_d30_360_eur, 1)
      )

    case 'ua_eltv_d40_error_rate':
      if (payload.data.ua_total_pred_variance_d40_360_eur == null) {
        payload.data.ua_total_pred_variance_d40_360_eur = 0
      }
      if (payload.data.ua_total_pred_d40_360_eur == null || payload.data.ua_total_pred_d40_360_eur <= 0) {
        return null
      }
      if (payload.data.max_dai < 41) {
        return null
      }
      return (
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d40_360_eur)) /
        Math.max(payload.data.ua_total_pred_d40_360_eur, 1)
      )

    case 'ua_eltv_d0_error_margin':
      if (payload.data.ua_total_pred_variance_d0_360_eur == null) {
        payload.data.ua_total_pred_variance_d0_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d0_360_eur)) / Math.max(payload.data.ua_installs, 1)

    case 'ua_eltv_d3_error_margin':
      if (payload.data.ua_total_pred_variance_d3_360_eur == null) {
        payload.data.ua_total_pred_variance_d3_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d3_360_eur)) / Math.max(payload.data.ua_installs, 1)

    case 'ua_eltv_d7_error_margin':
      if (payload.data.ua_total_pred_variance_d7_360_eur == null) {
        payload.data.ua_total_pred_variance_d7_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d7_360_eur)) / Math.max(payload.data.ua_installs, 1)

    case 'ua_eltv_d10_error_margin':
      if (payload.data.ua_total_pred_variance_d10_360_eur == null) {
        payload.data.ua_total_pred_variance_d10_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d10_360_eur)) / Math.max(payload.data.ua_installs, 1)

    case 'ua_eltv_d20_error_margin':
      if (payload.data.ua_total_pred_variance_d20_360_eur == null) {
        payload.data.ua_total_pred_variance_d20_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 21) {
        return null
      }
      return (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d20_360_eur)) / Math.max(payload.data.ua_installs, 1)

    case 'ua_eltv_d30_error_margin':
      if (payload.data.ua_total_pred_variance_d30_360_eur == null) {
        payload.data.ua_total_pred_variance_d30_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d30_360_eur)) / Math.max(payload.data.ua_installs, 1)

    case 'ua_eltv_d40_error_margin':
      if (payload.data.ua_total_pred_variance_d40_360_eur == null) {
        payload.data.ua_total_pred_variance_d40_360_eur = 0
      }
      if (payload.data.ua_installs == null || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 41) {
        return null
      }
      return (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_d40_360_eur)) / Math.max(payload.data.ua_installs, 1)

    case 'mwm_total_inapps_pred_d0_360_eur':
      if (payload.data.mwm_total_inapps_pred_d0_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.mwm_total_inapps_pred_d0_360_eur / payload.data.mwm_installs

    case 'mwm_total_inapps_pred_d3_360_eur':
      if (payload.data.mwm_total_inapps_pred_d3_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.mwm_total_inapps_pred_d3_360_eur / payload.data.mwm_installs

    case 'mwm_total_inapps_pred_d7_360_eur':
      if (payload.data.mwm_total_inapps_pred_d7_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.mwm_total_inapps_pred_d7_360_eur / payload.data.mwm_installs

    case 'mwm_total_inapps_pred_d10_360_eur':
      if (payload.data.mwm_total_inapps_pred_d10_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.mwm_total_inapps_pred_d10_360_eur / payload.data.mwm_installs

    case 'mwm_total_inapps_pred_d30_360_eur':
      if (payload.data.mwm_total_inapps_pred_d30_360_eur == null || payload.data.mwm_installs == null) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.mwm_total_inapps_pred_d30_360_eur / payload.data.mwm_installs

    case 'ua_ads_eltv_d30':
      if (payload.data.ua_total_ads_pred_d30_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_ads_pred_d30_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 31) {
        return null
      }
      return payload.data.ua_total_ads_pred_d30_360_eur / payload.data.ua_installs

    case 'ua_ads_eltv_d10':
      if (payload.data.ua_total_ads_pred_d10_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_ads_pred_d10_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }
      return payload.data.ua_total_ads_pred_d10_360_eur / payload.data.ua_installs

    case 'ua_ads_eltv_d7':
      if (payload.data.ua_total_ads_pred_d7_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_ads_pred_d7_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 8) {
        return null
      }
      return payload.data.ua_total_ads_pred_d7_360_eur / payload.data.ua_installs

    case 'ua_ads_eltv_d3':
      if (payload.data.ua_total_ads_pred_d3_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_ads_pred_d3_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 4) {
        return null
      }
      return payload.data.ua_total_ads_pred_d3_360_eur / payload.data.ua_installs

    case 'ua_ads_eltv_d0':
      if (payload.data.ua_total_ads_pred_d0_360_eur == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_total_ads_pred_d0_360_eur <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.max_dai < 1) {
        return null
      }
      return payload.data.ua_total_ads_pred_d0_360_eur / payload.data.ua_installs

    // case 'mwm_total_ads_pred_d0_360_eur':
    //   if (payload.data.mwm_total_ads_pred_d0_360_eur == null) {
    //     payload.data.mwm_total_ads_pred_d0_360_eur = 0
    //   }
    //   if (payload.data.max_dai < 1) {
    //     return null
    //   }
    //   return payload.data.mwm_total_ads_pred_d0_360_eur

    // case 'mwm_total_ads_pred_d3_360_eur':
    //   if (payload.data.mwm_total_ads_pred_d3_360_eur == null) {
    //     payload.data.mwm_total_ads_pred_d3_360_eur = 0
    //   }
    //   if (payload.data.max_dai < 4) {
    //     return null
    //   }
    //   return payload.data.mwm_total_ads_pred_d3_360_eur

    // case 'mwm_total_ads_pred_d7_360_eur':
    //   if (payload.data.mwm_total_ads_pred_d7_360_eur == null) {
    //     payload.data.mwm_total_ads_pred_d7_360_eur = 0
    //   }
    //   if (payload.data.max_dai < 8) {
    //     return null
    //   }
    //   return payload.data.mwm_total_ads_pred_d7_360_eur

    // case 'mwm_total_ads_pred_d10_360_eur':
    //   if (payload.data.mwm_total_ads_pred_d10_360_eur == null) {
    //     payload.data.mwm_total_ads_pred_d10_360_eur = 0
    //   }
    //   if (payload.data.max_dai < 41) {
    //     return null
    //   }
    //   return payload.data.mwm_total_ads_pred_d10_360_eur

    // case 'mwm_total_ads_pred_d30_360_eur':
    //   if (payload.data.mwm_total_ads_pred_d30_360_eur == null) {
    //     payload.data.mwm_total_ads_pred_d30_360_eur = 0
    //   }
    //   if (payload.data.max_dai < 31) {
    //     return null
    //   }
    //   return payload.data.mwm_total_ads_pred_d30_360_eur

    // case 'mwm_total_pred_d0_360_eur':
    //   if (payload.data.mwm_total_pred_d0_360_eur == null) {
    //     payload.data.mwm_total_pred_d0_360_eur = 0
    //   }
    //   if (payload.data.max_dai < 1) {
    //     return null
    //   }
    //   return payload.data.mwm_total_pred_d0_360_eur

    // case 'mwm_total_pred_d3_360_eur':
    //   if (payload.data.mwm_total_pred_d3_360_eur == null) {
    //     payload.data.mwm_total_pred_d3_360_eur = 0
    //   }
    //   if (payload.data.max_dai < 4) {
    //     return null
    //   }
    //   return payload.data.mwm_total_pred_d3_360_eur

    // case 'mwm_total_pred_d7_360_eur':
    //   if (payload.data.mwm_total_pred_d7_360_eur == null) {
    //     payload.data.mwm_total_pred_d7_360_eur = 0
    //   }
    //   if (payload.data.max_dai < 8) {
    //     return null
    //   }
    //   return payload.data.mwm_total_pred_d7_360_eur

    // case 'mwm_total_pred_d10_360_eur':
    //   if (payload.data.mwm_total_pred_d10_360_eur == null) {
    //     payload.data.mwm_total_pred_d10_360_eur = 0
    //   }
    //   if (payload.data.max_dai < 11) {
    //     return null
    //   }
    //   return payload.data.mwm_total_pred_d10_360_eur

    // case 'mwm_total_pred_d30_360_eur':
    //   if (payload.data.mwm_total_pred_d30_360_eur == null) {
    //     payload.data.mwm_total_pred_d30_360_eur = 0
    //   }
    //   if (payload.data.max_dai < 31) {
    //     return null
    //   }
    //   return payload.data.mwm_total_pred_d30_360_eur

    case 'ua_total_pred_dmax_360_eur':
      return payload.data.ua_total_pred_dmax_360_eur

    case 'hint_val_per_user': {
      if (payload.data.ua_spend == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_spend <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      // let predLTVPerUser = payload.data.ua_total_pred_dmax_360_eur / payload.data.ua_installs
      return (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_dmax_360_eur)) / payload.data.ua_installs
    }

    case 'hint_max_per_user': {
      if (payload.data.ua_spend == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_spend <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      const predLTVPerUser = payload.data.ua_total_pred_dmax_360_eur / payload.data.ua_installs
      const eLTVErrorMarginPerUser =
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_dmax_360_eur)) / payload.data.ua_installs
      return predLTVPerUser + eLTVErrorMarginPerUser
    }

    case 'hint_min_per_user': {
      if (payload.data.ua_spend == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_spend <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      const predLTVPerUser = payload.data.ua_total_pred_dmax_360_eur / payload.data.ua_installs
      const eLTVErrorMarginPerUser =
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_dmax_360_eur)) / payload.data.ua_installs
      return predLTVPerUser - eLTVErrorMarginPerUser
    }

    case 'hint_proportion': {
      if (payload.data.ua_spend == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_spend <= 0 || payload.data.ua_installs <= 0) {
        return null
      }
      const predLTVPerUser = payload.data.ua_total_pred_dmax_360_eur / payload.data.ua_installs
      const eLTVErrorMarginPerUser =
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_dmax_360_eur)) / payload.data.ua_installs
      if (predLTVPerUser <= 0) {
        return 0
      }
      return eLTVErrorMarginPerUser / predLTVPerUser
    }

    case 'hint': {
      if (payload.data.ua_spend == null || payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_spend <= 0 || payload.data.ua_installs <= 0) {
        return null
      }

      if (payload.data.mwm_installs < 100) {
        return 'empty'
      }
      if (payload.data.max_dai < 11) {
        return 'wait'
      }

      const predLTVPerUser = payload.data.ua_total_pred_dmax_360_eur / payload.data.ua_installs
      const eLTVErrorMarginPerUser =
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_dmax_360_eur)) / payload.data.ua_installs
      const cpi = payload.data.ua_spend / payload.data.ua_installs
      if (predLTVPerUser <= 0) {
        return ''
      }

      if (predLTVPerUser + eLTVErrorMarginPerUser < cpi) {
        return 'rip'
      } else if (predLTVPerUser - eLTVErrorMarginPerUser > cpi) {
        return 'top'
      } else if (eLTVErrorMarginPerUser / predLTVPerUser > 0.5) {
        return 'warning'
      }
      return ''
    }

    case 'breakeven_probability': {
      payload.data.breakeven_probability = []

      if (payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.mwm_installs < 100) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }

      const predLTVPerUser = payload.data.ua_total_pred_dmax_360_eur / payload.data.ua_installs
      const eLTVErrorMarginPerUser =
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_dmax_360_eur)) / payload.data.ua_installs
      const cpi = payload.data.ua_spend / payload.data.ua_installs
      if (eLTVErrorMarginPerUser <= 0) {
        return -1
      }

      const sigma = eLTVErrorMarginPerUser / 1.96
      const delta = predLTVPerUser - cpi
      const z0 = delta / sigma
      const zScore = [computeZscore(z0)]
      payload.data.breakeven_probability = zScore
      return zScore
    }

    case 'margin_minus_10_probability': {
      payload.data.margin_minus_10_probability = []

      if (payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.mwm_installs < 100) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }

      const predLTVPerUser = payload.data.ua_total_pred_dmax_360_eur / payload.data.ua_installs
      const eLTVErrorMarginPerUser =
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_dmax_360_eur)) / payload.data.ua_installs
      const cpi = (payload.data.ua_spend / payload.data.ua_installs) * 0.9
      if (eLTVErrorMarginPerUser <= 0) {
        return -1
      }

      const sigma = eLTVErrorMarginPerUser / 1.96
      const delta = predLTVPerUser - cpi
      const z0 = delta / sigma
      const zScore = [1 - computeZscore(z0)]
      payload.data.margin_minus_10_probability = zScore
      return zScore
    }

    case 'margin_10_probability': {
      payload.data.margin_10_probability = []

      if (payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.mwm_installs < 100) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }

      const predLTVPerUser = payload.data.ua_total_pred_dmax_360_eur / payload.data.ua_installs
      const eLTVErrorMarginPerUser =
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_dmax_360_eur)) / payload.data.ua_installs
      const cpi = (payload.data.ua_spend / payload.data.ua_installs) * 1.1
      if (eLTVErrorMarginPerUser <= 0) {
        return -1
      }

      const sigma = eLTVErrorMarginPerUser / 1.96
      const delta = predLTVPerUser - cpi
      const z0 = delta / sigma
      const zScore = [computeZscore(z0)]
      payload.data.margin_10_probability = zScore
      return zScore
    }

    case 'margin_25_probability': {
      payload.data.margin_25_probability = []

      if (payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.mwm_installs < 100) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }

      const predLTVPerUser = payload.data.ua_total_pred_dmax_360_eur / payload.data.ua_installs
      const eLTVErrorMarginPerUser =
        (1.96 * Math.sqrt(payload.data.ua_total_pred_variance_dmax_360_eur)) / payload.data.ua_installs
      const cpi = (payload.data.ua_spend / payload.data.ua_installs) * 1.25
      if (eLTVErrorMarginPerUser <= 0) {
        return -1
      }

      const sigma = eLTVErrorMarginPerUser / 1.96
      const delta = predLTVPerUser - cpi
      const z0 = delta / sigma
      const zScore = [computeZscore(z0)]
      payload.data.margin_25_probability = zScore
      return zScore
    }

    case 'arpu_vs_ltv': {
      if (payload.data.ua_installs == null) {
        return null
      }
      if (payload.data.ua_installs <= 0) {
        return null
      }
      if (payload.data.mwm_installs < 100) {
        return null
      }
      if (payload.data.max_dai < 11) {
        return null
      }

      const predLTVPerUser = payload.data.ua_total_pred_dmax_360_eur / payload.data.ua_installs
      const arpu =
        (payload.data.ua_ads_revenue_total_eur + payload.data.ua_inapps_revenue_total_eur) / payload.data.ua_installs

      if (arpu > predLTVPerUser) {
        return 'warning'
      }
      return 'legit'
    }

    case 'view_more': {
      return 'view_more'
    }

    default:
      return 'unkown KPI'
  }
}

export default {
  buildKpi,
}
